import {
  AUTOMATION_ENQUEUE_WHITELIST,
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  type AutomationStepType,
} from '@marketing-hub/shared';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import leadStyles from '@/features/leads/Leads.module.css';

export type EditableAutomationStep = {
  step_order: number;
  step_type: AutomationStepType;
  config: Record<string, unknown>;
  enabled?: boolean;
};

type AgentOption = { id: string; name: string; enabled: boolean };

type Props = {
  step: EditableAutomationStep;
  agents: AgentOption[];
  onChange: (step: EditableAutomationStep) => void;
};

export function AutomationStepConfigEditor({ step, agents, onChange }: Props) {
  const cfg = step.config;

  function setConfig(patch: Record<string, unknown>) {
    onChange({ ...step, config: { ...cfg, ...patch } });
  }

  switch (step.step_type) {
    case 'lead_add_tag':
      return (
        <Field label="Tag">
          <TextInput
            value={String(cfg.tag ?? '')}
            onChange={(e) => setConfig({ tag: e.target.value })}
          />
        </Field>
      );
    case 'lead_set_status':
      return (
        <Field label="Status">
          <select
            className={leadStyles.filterInput}
            value={String(cfg.status ?? 'contacted')}
            onChange={(e) => setConfig({ status: e.target.value })}
          >
            {LEAD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {LEAD_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
      );
    case 'lead_append_note':
      return (
        <>
          <Field label="Notitie">
            <TextArea
              rows={3}
              value={String(cfg.body ?? '')}
              onChange={(e) => setConfig({ body: e.target.value })}
            />
          </Field>
          <label>
            <input
              type="checkbox"
              checked={cfg.also_append_leads_notes === true}
              onChange={(e) => setConfig({ also_append_leads_notes: e.target.checked })}
            />{' '}
            Ook in leads.notes
          </label>
        </>
      );
    case 'emit_webhook':
      return (
        <label>
          <input
            type="checkbox"
            checked={cfg.include_trigger_payload !== false}
            onChange={(e) => setConfig({ include_trigger_payload: e.target.checked })}
          />{' '}
          Trigger payload meesturen
        </label>
      );
    case 'enqueue_job':
      return (
        <Field label="Job">
          <select
            className={leadStyles.filterInput}
            value={String(cfg.job_name ?? AUTOMATION_ENQUEUE_WHITELIST[0])}
            onChange={(e) => setConfig({ job_name: e.target.value })}
          >
            {AUTOMATION_ENQUEUE_WHITELIST.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>
        </Field>
      );
    case 'run_agent':
      return (
        <Field label="Agent">
          <select
            className={leadStyles.filterInput}
            value={String(cfg.agent_id ?? '')}
            onChange={(e) => setConfig({ agent_id: e.target.value })}
          >
            <option value="">— kies agent —</option>
            {agents
              .filter((a) => a.enabled)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </Field>
      );
    case 'draft_reply':
      return (
        <>
          <label>
            <input
              type="checkbox"
              checked={cfg.require_approval !== false}
              onChange={(e) => setConfig({ require_approval: e.target.checked })}
            />{' '}
            Menselijke goedkeuring vereist (HITL)
          </label>
          <Field label="Onderwerp (optioneel)">
            <TextInput
              value={String(cfg.subject ?? '')}
              onChange={(e) => setConfig({ subject: e.target.value })}
            />
          </Field>
        </>
      );
    case 'generate_analytics_report':
      return (
        <Field label="Periode (dagen)">
          <TextInput
            type="number"
            min={1}
            max={90}
            value={String(cfg.days ?? 14)}
            onChange={(e) => setConfig({ days: Number(e.target.value) || 14 })}
          />
        </Field>
      );
    default:
      return null;
  }
}
